import { useCallback } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getMyReferralCode, sendSignupReferralInvite } from './api';
import type { ReferralCodeResponse, SendInviteResponse } from './types';

export const signupReferralQueryKeys = {
  all: ['signup-referral'] as const,
  myCode: () => [...signupReferralQueryKeys.all, 'my-code'] as const
};

export const useMyReferralCode = (enabled = true) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  return useQuery<ReferralCodeResponse, Error>({
    queryKey: signupReferralQueryKeys.myCode(),
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      return getMyReferralCode({ accessToken });
    },
    enabled: enabled && isAuthenticated,
    staleTime: 60_000,
    retry: (failureCount, error) => {
      if ((error as { error?: string })?.error === 'feature_disabled') return false;
      return failureCount < 2;
    }
  });
};

// Imperative cache-first read for copy-time use: instant when the mount-time
// query has resolved, joins the in-flight fetch when it hasn't — so auto-copied
// links never race the code load.
export const useEnsureMyReferralCode = () => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const queryClient = useQueryClient();

  return useCallback(async (): Promise<string | null> => {
    if (!isAuthenticated) return null;
    try {
      const data = await queryClient.ensureQueryData<ReferralCodeResponse>({
        queryKey: signupReferralQueryKeys.myCode(),
        queryFn: async () => {
          const accessToken = await getAccessTokenSilently();
          return getMyReferralCode({ accessToken });
        }
      });
      return data?.code ?? null;
    } catch {
      return null;
    }
  }, [getAccessTokenSilently, isAuthenticated, queryClient]);
};

export const useSendSignupReferralInvite = () => {
  const { getAccessTokenSilently } = useAuth0();
  return useMutation<SendInviteResponse, Error, { email: string; name?: string }>({
    mutationFn: async ({ email, name }) => {
      const accessToken = await getAccessTokenSilently();
      return sendSignupReferralInvite({ accessToken, email, name });
    }
  });
};
